import { useEffect, useMemo, useRef, useState } from "react";
import { getCalendar } from "../../request/calendar";
import "./styles.scss";

const LOCATION_ORDER = [
  "Remote",
  "Poland",
  "Bazhana",
  "Wall Street",
  "Onlytower",
];
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const STATUS_KEYS = { "Sick Leave": "sick", Vacation: "vacation" };
const TYPE_ALIASES = {
  "sick leave": "Sick Leave",
  vacation: "Vacation",
};
const FILTERS_STORAGE_KEY = "es-calendar-v2-filters";

function nameOf(email = "") {
  return email.split("@")[0] || email;
}

function slug(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function keyOf(row) {
  if (row.type === "L1") return "l1";
  if (STATUS_KEYS[row.type]) return STATUS_KEYS[row.type];
  if (!row.type && row.pd) return "night";
  return slug(row.location);
}

function sortKey(row) {
  if (row.type === "L1")
    return [0, "", row.timeStartText || "", nameOf(row.email)];
  if (STATUS_KEYS[row.type]) return [3, row.type, "", nameOf(row.email)];
  if (!row.type && row.pd)
    return [2, "", row.timeStartText || "", nameOf(row.email)];
  return [1, row.location || "", row.timeStartText || "", nameOf(row.email)];
}

function dayCmp(a, b) {
  const ka = sortKey(a);
  const kb = sortKey(b);
  return (
    ka[0] - kb[0] ||
    ka[1].localeCompare(kb[1]) ||
    ka[2].localeCompare(kb[2]) ||
    ka[3].localeCompare(kb[3])
  );
}

function parseDate(value) {
  if (!value) return null;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    const [day, month, year] = value.split(".").map(Number);
    return new Date(year, month - 1, day);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function fmtISO(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function mondayOf(date) {
  const result = new Date(date);
  const day = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - day);
  result.setHours(0, 0, 0, 0);
  return result;
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addMonths(date, months) {
  const result = new Date(date);
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  return result;
}

function formatTime(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number")
    return `${String(value).padStart(2, "0")}:00`;

  const text = String(value).trim();
  if (/^\d{1,2}$/.test(text)) return `${text.padStart(2, "0")}:00`;

  const timeMatch = text.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
  if (timeMatch) {
    const [, hour, minute] = timeMatch;
    return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
  }

  return text;
}

function formatTimeRange(row) {
  if (!row.timeStartText || !row.timeEndText) return "";
  return `${row.timeStartText}–${row.timeEndText}`;
}

function normalizeOptionalText(value) {
  if (value === null || value === undefined) return "";

  const text = String(value).trim();
  return ["undefined", "null"].includes(text.toLowerCase()) ? "" : text;
}

function normalizeType(value) {
  const type = normalizeOptionalText(value);
  return TYPE_ALIASES[type.toLowerCase()] || type;
}

function normalizeRow(row) {
  const date = parseDate(row.date);
  if (!date) return null;

  return {
    ...row,
    email: normalizeOptionalText(row.email),
    type: normalizeType(row.type),
    location: normalizeOptionalText(row.location),
    onboarding: toBool(row.onboarding),
    pd: toBool(row.pd),
    isoDate: fmtISO(date),
    dateObject: date,
    timeStartText: formatTime(row.timeStart ?? row.time_start),
    timeEndText: formatTime(row.timeEnd ?? row.time_end),
  };
}

function toBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string")
    return ["1", "true", "yes"].includes(value.toLowerCase());
  return false;
}

function getInitialTheme() {
  const stored = localStorage.getItem("es-theme");
  if (stored) return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getInitialView() {
  return localStorage.getItem("es-view") || "week";
}

function getToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function buildOptions(rows) {
  const types = [
    ...new Set(rows.map((row) => row.type).filter(Boolean)),
  ].sort();
  const locations = LOCATION_ORDER.filter((location) =>
    rows.some((row) => row.location === location),
  ).concat(
    [...new Set(rows.map((row) => row.location))].filter(
      (location) => location && !LOCATION_ORDER.includes(location),
    ),
  );
  const employees = [...new Set(rows.map((row) => nameOf(row.email)))].sort();
  const shifts = [
    ...new Set(
      rows
        .filter((row) => row.timeStartText && row.timeEndText)
        .map((row) => `${row.timeStartText}-${row.timeEndText}`),
    ),
  ].sort();

  return { types, locations, employees, shifts };
}

function passesFilters(row, filters) {
  if (filters.types.length && !filters.types.includes(row.type)) return false;
  if (filters.locations.length && !filters.locations.includes(row.location))
    return false;
  if (
    filters.employees.length &&
    !filters.employees.includes(nameOf(row.email))
  )
    return false;
  if (filters.shifts.length) {
    const shift =
      row.timeStartText && row.timeEndText
        ? `${row.timeStartText}-${row.timeEndText}`
        : "";
    if (!filters.shifts.includes(shift)) return false;
  }
  if (
    filters.onboarding !== "all" &&
    row.onboarding !== (filters.onboarding === "yes")
  )
    return false;
  if (filters.pd !== "all" && row.pd !== (filters.pd === "yes")) return false;
  return true;
}

const initialFilters = {
  types: [],
  locations: [],
  employees: [],
  shifts: [],
  onboarding: "all",
  pd: "all",
};

function getInitialFilters() {
  try {
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!stored) return initialFilters;

    const parsed = JSON.parse(stored);
    return {
      types: Array.isArray(parsed.types) ? parsed.types : [],
      locations: Array.isArray(parsed.locations) ? parsed.locations : [],
      employees: Array.isArray(parsed.employees) ? parsed.employees : [],
      shifts: Array.isArray(parsed.shifts) ? parsed.shifts : [],
      onboarding: ["all", "yes", "no"].includes(parsed.onboarding)
        ? parsed.onboarding
        : "all",
      pd: ["all", "yes", "no"].includes(parsed.pd) ? parsed.pd : "all",
    };
  } catch (err) {
    console.error(err);
    return initialFilters;
  }
}

export default function CalendarPageV2() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setViewState] = useState(getInitialView);
  const [theme, setThemeState] = useState(getInitialTheme);
  const [refDate, setRefDate] = useState(getToday);
  const [filters, setFilters] = useState(getInitialFilters);
  const [filteredRows, setFilteredRows] = useState([]);
  const [popover, setPopover] = useState(null);

  const today = useMemo(getToday, []);
  const options = useMemo(() => buildOptions(rows), [rows]);

  useEffect(() => {
    setFilteredRows(rows.filter((row) => passesFilters(row, filters)));
  }, [rows, filters]);

  useEffect(() => {
    let isMounted = true;

    const loadCalendar = () => {
      getCalendar()
        .then(({ status, data }) => {
          if (!isMounted) return;

          if (status === 200 && Array.isArray(data)) {
            const normalized = data.map(normalizeRow).filter(Boolean);
            setRows(normalized);
            setError("");
          } else {
            setError("Error downloading calendar, please reload page.");
          }
        })
        .catch((err) => {
          if (!isMounted) return;
          console.error(err);
          setError("Error downloading calendar, please reload page.");
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    };

    loadCalendar();
    const intervalId = window.setInterval(loadCalendar, 60000 * 5);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("es-theme", theme);
    document.body.classList.toggle("calendar-page-v2-dark", theme === "dark");
    document.body.classList.toggle("calendar-page-v2-light", theme === "light");

    return () => {
      document.body.classList.remove(
        "calendar-page-v2-dark",
        "calendar-page-v2-light",
      );
    };
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("es-view", view);
  }, [view]);

  useEffect(() => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const setView = (nextView) => {
    setViewState(nextView);
    setPopover(null);
  };

  const nav = (direction) => {
    setRefDate((current) =>
      view === "week"
        ? addDays(mondayOf(current), direction * 7)
        : addMonths(current, direction),
    );
    setPopover(null);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setPopover(null);
  };

  const updateMultiFilter = (key, value, checked) => {
    setFilters((current) => {
      const selected = new Set(current[key]);
      if (checked) selected.add(value);
      else selected.delete(value);
      return { ...current, [key]: [...selected] };
    });
  };

  const updateTriFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <section className="calendar-page-v2" data-theme={theme}>
      <header className="schedule-top">
        <div className="schedule-top-row">
          <div className="schedule-brand">
            <h1>Engineer Schedule</h1>
            <span className="schedule-week-label">
              {view === "week"
                ? formatWeekLabel(refDate)
                : formatMonthLabel(refDate)}
            </span>
          </div>
          <div className="schedule-nav">
            <button
              className="schedule-btn"
              type="button"
              onClick={() => setRefDate(today)}
            >
              Today
            </button>
            <button
              className="schedule-btn schedule-icon-btn"
              type="button"
              onClick={() => nav(-1)}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className="schedule-btn schedule-icon-btn"
              type="button"
              onClick={() => nav(1)}
              aria-label="Next"
            >
              ›
            </button>
          </div>
          <SegmentedControl
            label="View"
            value={view}
            items={[
              ["week", "Week"],
              ["month", "Month"],
            ]}
            onChange={setView}
          />
          <button
            className="schedule-btn"
            type="button"
            onClick={() =>
              setThemeState((current) =>
                current === "dark" ? "light" : "dark",
              )
            }
          >
            {theme === "dark" ? "Light" : "Dark"}
          </button>
        </div>

        <div className="schedule-filters">
          <FilterDropdown
            label="Type"
            labelName="types"
            options={options.types}
            values={filters.types}
            onChange={(value, checked) =>
              updateMultiFilter("types", value, checked)
            }
            onSelectAll={() =>
              setFilters((current) => ({ ...current, types: options.types }))
            }
            onClear={() => setFilters((current) => ({ ...current, types: [] }))}
            swatchType="types"
          />
          <FilterDropdown
            label="Location"
            labelName="locations"
            options={options.locations}
            values={filters.locations}
            onChange={(value, checked) =>
              updateMultiFilter("locations", value, checked)
            }
            onSelectAll={() =>
              setFilters((current) => ({
                ...current,
                locations: options.locations,
              }))
            }
            onClear={() =>
              setFilters((current) => ({ ...current, locations: [] }))
            }
            swatchType="locations"
          />
          <FilterDropdown
            label="Employee"
            labelName="employees"
            options={options.employees}
            values={filters.employees}
            onChange={(value, checked) =>
              updateMultiFilter("employees", value, checked)
            }
            onSelectAll={() =>
              setFilters((current) => ({
                ...current,
                employees: options.employees,
              }))
            }
            onClear={() =>
              setFilters((current) => ({ ...current, employees: [] }))
            }
            searchable
          />
          <FilterDropdown
            label="Time"
            labelName="times"
            options={options.shifts}
            values={filters.shifts}
            onChange={(value, checked) =>
              updateMultiFilter("shifts", value, checked)
            }
            onSelectAll={() =>
              setFilters((current) => ({ ...current, shifts: options.shifts }))
            }
            onClear={() =>
              setFilters((current) => ({ ...current, shifts: [] }))
            }
            searchable
          />
          <TriFilter
            label="Onboarding"
            value={filters.onboarding}
            onChange={(value) => updateTriFilter("onboarding", value)}
          />
          <TriFilter
            label="On-duty (PD)"
            value={filters.pd}
            onChange={(value) => updateTriFilter("pd", value)}
          />
          <div className="schedule-field">
            <label>&nbsp;</label>
            <button
              className="schedule-btn schedule-ghost-btn"
              type="button"
              onClick={resetFilters}
            >
              Reset filters
            </button>
          </div>
        </div>
      </header>

      <main className="schedule-main">
        {loading && <div className="schedule-state">Loading calendar...</div>}
        {!loading && error && <div className="schedule-state">{error}</div>}
        {!loading && !error && view === "week" && (
          <WeekView
            rows={filteredRows}
            allRows={rows}
            refDate={refDate}
            today={today}
          />
        )}
        {!loading && !error && view === "month" && (
          <MonthView
            rows={filteredRows}
            refDate={refDate}
            today={today}
            popover={popover}
            setPopover={setPopover}
          />
        )}
      </main>

      {popover && <RosterPopover popover={popover} />}
    </section>
  );
}

function formatWeekLabel(refDate) {
  const monday = mondayOf(refDate);
  const sunday = addDays(monday, 6);
  if (monday.getMonth() === sunday.getMonth()) {
    return `${MONTHS[monday.getMonth()]} ${monday.getDate()}-${sunday.getDate()}, ${monday.getFullYear()}`;
  }
  return `${MONTHS[monday.getMonth()]} ${monday.getDate()} - ${MONTHS[sunday.getMonth()]} ${sunday.getDate()}, ${sunday.getFullYear()}`;
}

function formatMonthLabel(refDate) {
  return `${MONTHS[refDate.getMonth()]} ${refDate.getFullYear()}`;
}

function FilterDropdown({
  label,
  labelName,
  options,
  values,
  onChange,
  onSelectAll,
  onClear,
  searchable,
  swatchType,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const visibleOptions = options.filter((option) =>
    option.toLowerCase().includes(query.trim().toLowerCase()),
  );

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
    };
  }, [open]);

  return (
    <div className="schedule-field">
      <label>{label}</label>
      <div ref={rootRef} className={`schedule-dd${open ? " open" : ""}`}>
        <button
          className="schedule-dd-btn"
          type="button"
          onClick={() => setOpen((current) => !current)}
        >
          {values.length ? (
            <>
              {capitalize(labelName)}{" "}
              <span className="schedule-count">· {values.length}</span>
            </>
          ) : (
            `All ${labelName}`
          )}
        </button>
        {open && (
          <div className="schedule-dd-panel">
            {searchable && (
              <input
                className="schedule-dd-search"
                value={query}
                placeholder="Search"
                onChange={(event) => setQuery(event.target.value)}
              />
            )}
            <div className="schedule-dd-tools">
              <button type="button" onClick={onSelectAll}>
                Select all
              </button>
              <button type="button" onClick={onClear}>
                Clear
              </button>
            </div>
            <div className="schedule-dd-list">
              {visibleOptions.map((option) => (
                <label className="schedule-opt" key={option}>
                  <input
                    type="checkbox"
                    checked={values.includes(option)}
                    onChange={(event) => onChange(option, event.target.checked)}
                  />
                  <Swatch value={option} type={swatchType} />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function Swatch({ value, type }) {
  let key = "";
  if (type === "types") key = value === "L1" ? "l1" : STATUS_KEYS[value] || "";
  if (type === "locations") key = slug(value);
  if (!key) return null;
  return <span className={`schedule-swatch k-${key}`} />;
}

function SegmentedControl({ label, value, items, onChange }) {
  return (
    <div className="schedule-seg" role="group" aria-label={label}>
      {items.map(([itemValue, itemLabel]) => (
        <button
          key={itemValue}
          type="button"
          className={value === itemValue ? "on" : ""}
          onClick={() => onChange(itemValue)}
        >
          {itemLabel}
        </button>
      ))}
    </div>
  );
}

function TriFilter({ label, value, onChange }) {
  return (
    <div className="schedule-field">
      <label>{label}</label>
      <SegmentedControl
        label={label}
        value={value}
        onChange={onChange}
        items={[
          ["all", "All"],
          ["yes", "Yes"],
          ["no", "No"],
        ]}
      />
    </div>
  );
}

function WeekView({ rows, allRows, refDate, today }) {
  const monday = mondayOf(refDate);

  return (
    <div className="schedule-grid">
      {WEEK_DAYS.map((dayName, index) => {
        const day = addDays(monday, index);
        const iso = fmtISO(day);
        const dayRows = rows.filter((row) => row.isoDate === iso).sort(dayCmp);
        const hasRowsWithoutFilters = allRows.some(
          (row) => row.isoDate === iso,
        );

        return (
          <div
            className={`schedule-col${index >= 5 ? " weekend" : ""}${day.getTime() === today.getTime() ? " today" : ""}`}
            key={iso}
          >
            <div className="schedule-day-head">
              <span className="schedule-wd">{dayName}</span>
              <span className="schedule-dt">
                {String(day.getDate()).padStart(2, "0")}
              </span>
              {dayRows.length > 0 && (
                <span className="schedule-num">{dayRows.length}</span>
              )}
            </div>
            <div className="schedule-day-list">
              {dayRows.length ? (
                dayRows.map((row, rowIndex) => (
                  <Plaque
                    row={row}
                    key={`${row.isoDate}-${row.email}-${rowIndex}`}
                  />
                ))
              ) : (
                <div className="schedule-empty">
                  {hasRowsWithoutFilters ? "No matches" : "No shifts"}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Plaque({ row }) {
  const status = STATUS_KEYS[row.type];
  const key = keyOf(row);

  if (status) {
    const label = row.type === "Sick Leave" ? "Sick leave" : "On vacation";
    return (
      <div
        className={`schedule-plaque status k-${key}`}
        title={`${row.email} - ${label}`}
      >
        <div className="schedule-p-row1">
          <span className="schedule-p-name">{nameOf(row.email)}</span>
        </div>
        <div className="schedule-p-row2 schedule-p-status">
          {row.type === "Sick Leave" && (
            <span className="schedule-p-ico">🤒</span>
          )}
          {row.type === "Vacation" && (
            <span className="schedule-p-ico">🌴</span>
          )}
          <span className="schedule-p-statuslabel">{label}</span>
        </div>
      </div>
    );
  }

  if (!row.type && row.pd) {
    const timeText = formatTimeRange(row) || "Night on-duty";
    return (
      <div
        className={`schedule-plaque status night k-${key}`}
        title={`${row.email} - Night on-duty`}
      >
        <div className="schedule-p-row1">
          <span className="schedule-p-name">{nameOf(row.email)}</span>
          <span className="schedule-p-badges">
            <span className="schedule-badge b-pd">PD</span>
          </span>
        </div>
        <div className="schedule-p-row2 schedule-p-status">
          <span className="schedule-p-ico">🌙</span>
          <span className="schedule-p-statuslabel">{timeText}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`schedule-plaque k-${key}`}
      title={`${row.email} - ${row.type} - ${row.location} - ${row.timeStartText}-${row.timeEndText}`}
    >
      <div className="schedule-p-row1">
        <span className="schedule-p-name">{nameOf(row.email)}</span>
        <span className="schedule-p-badges">
          {row.onboarding && <span className="schedule-badge b-onb">OnB</span>}
          {row.pd && <span className="schedule-badge b-pd">PD</span>}
        </span>
      </div>
      <div className="schedule-p-row2">
        <span className="schedule-p-type">{row.type}</span>
        <span className="schedule-p-time">
          {row.timeStartText}-{row.timeEndText}
        </span>
        <span className="schedule-p-loc">{row.location}</span>
      </div>
    </div>
  );
}

function MonthView({ rows, refDate, today, popover, setPopover }) {
  const year = refDate.getFullYear();
  const month = refDate.getMonth();
  const days = Array.from(
    { length: new Date(year, month + 1, 0).getDate() },
    (_, index) => new Date(year, month, index + 1),
  );
  const byEmployee = useMemo(() => {
    return rows.reduce((acc, row) => {
      if (
        row.dateObject.getFullYear() !== year ||
        row.dateObject.getMonth() !== month
      )
        return acc;
      const employee = nameOf(row.email);
      acc[employee] = acc[employee] || {};
      acc[employee][row.isoDate] = acc[employee][row.isoDate] || [];
      acc[employee][row.isoDate].push(row);
      return acc;
    }, {});
  }, [rows, year, month]);
  const employees = Object.keys(byEmployee).sort();

  return (
    <div className="schedule-month-view">
      <Legend />
      <div
        className="schedule-roster-wrap"
        onScroll={() => popover && setPopover(null)}
      >
        <table className="schedule-roster">
          {employees.length ? (
            <>
              <thead>
                <tr>
                  <th className="schedule-cnr">Engineer</th>
                  {days.map((day) => {
                    const dow = (day.getDay() + 6) % 7;
                    return (
                      <th
                        className={`schedule-d${dow >= 5 ? " weekend" : ""}${day.getTime() === today.getTime() ? " today" : ""}`}
                        key={fmtISO(day)}
                      >
                        <span className="schedule-wd">{WEEK_DAYS[dow][0]}</span>
                        <span className="schedule-dn">
                          {String(day.getDate()).padStart(2, "0")}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee}>
                    <th className="schedule-name" title={employee}>
                      {employee}
                    </th>
                    {days.map((day) => {
                      const dow = (day.getDay() + 6) % 7;
                      const iso = fmtISO(day);
                      const entries = byEmployee[employee][iso]?.sort(dayCmp);
                      return (
                        <td
                          className={`schedule-cell${dow >= 5 ? " weekend" : ""}${day.getTime() === today.getTime() ? " today" : ""}`}
                          key={iso}
                        >
                          {entries && (
                            <RosterChip
                              entries={entries}
                              setPopover={setPopover}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </>
          ) : (
            <tbody>
              <tr>
                <td className="schedule-cell empty-month">
                  No matches this month
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}

function Legend() {
  const items = [
    ["l1", "L1"],
    ["remote", "Remote"],
    ["poland", "Poland"],
    ["bazhana", "Bazhana"],
    ["wallstreet", "Wall Street"],
    ["onlytower", "Onlytower"],
    ["sick", "Sick leave"],
    ["vacation", "Vacation"],
    ["night", "Night on-duty"],
  ];

  return (
    <div className="schedule-roster-legend">
      {items.map(([key, label]) => (
        <span className="schedule-lg" key={key}>
          <span className={`schedule-swatch k-${key}`} />
          {label}
        </span>
      ))}
      <span className="schedule-lg">
        <span className="schedule-dot onb" />
        OnB
      </span>
      <span className="schedule-lg">
        <span className="schedule-dot pd" />
        PD
      </span>
    </div>
  );
}

function RosterChip({ entries, setPopover }) {
  const row = entries[0];
  let label = row.type;
  if (!row.type && row.pd) label = "N";
  if (row.type === "Sick Leave") label = "S";
  if (row.type === "Vacation") label = "V";

  const showPopover = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPopover({
      entries,
      left: Math.max(
        8,
        Math.min(rect.left + rect.width / 2, window.innerWidth - 280),
      ),
      top: rect.top,
    });
  };

  return (
    <button
      type="button"
      className={`schedule-chip k-${keyOf(row)}`}
      onMouseEnter={showPopover}
      onFocus={showPopover}
      onMouseLeave={() => setPopover(null)}
      onBlur={() => setPopover(null)}
      onClick={showPopover}
    >
      {label}
      {row.onboarding && <span className="schedule-dot onb" />}
      {row.pd && row.type && <span className="schedule-dot pd" />}
      {entries.length > 1 && (
        <span className="schedule-more">+{entries.length - 1}</span>
      )}
    </button>
  );
}

function RosterPopover({ popover }) {
  return (
    <div
      className="schedule-pop show"
      style={{ left: popover.left, top: popover.top }}
    >
      {popover.entries.map((entry, index) => {
        const isNight = !entry.type && entry.pd;
        const kind = isNight
          ? "Night on-duty"
          : entry.type === "Vacation"
            ? "On vacation"
            : entry.type || "Shift";

        return (
          <div
            className="schedule-pop-entry"
            key={`${entry.email}-${entry.isoDate}-${index}`}
          >
            <div className="schedule-pop-row">
              <b>{nameOf(entry.email)}</b>
              <span className="schedule-pop-mail">{entry.email}</span>
            </div>
            <div className="schedule-pop-meta">
              <span className={`schedule-pop-kind k-${keyOf(entry)}`}>
                {kind}
              </span>
              {entry.location && (
                <span className="schedule-pop-loc">{entry.location}</span>
              )}
              {entry.timeStartText && entry.timeEndText && (
                <span className="schedule-pop-time">
                  {entry.timeStartText}-{entry.timeEndText}
                </span>
              )}
            </div>
            {(entry.onboarding || (entry.pd && entry.type)) && (
              <div className="schedule-pop-flags">
                {entry.onboarding && (
                  <span className="schedule-badge b-onb">OnB</span>
                )}
                {entry.pd && entry.type && (
                  <span className="schedule-badge b-pd">PD</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
